'use client';

/**
 * MutationButton Component
 *
 * A button that displays mutation state progression (Stripe-inspired).
 * Shows loading spinner, success checkmark, or error state with automatic reset.
 *
 * Usage:
 * ```tsx
 * const [state, setState] = useState<MutationState>('idle');
 *
 * const handleSave = async () => {
 *   setState('loading');
 *   try {
 *     await saveData();
 *     setState('success');
 *   } catch {
 *     setState('error');
 *   }
 * };
 *
 * <MutationButton
 *   state={state}
 *   idleText="Save changes"
 *   loadingText="Saving..."
 *   successText="Saved!"
 *   errorText="Failed"
 *   onClick={handleSave}
 * />
 * ```
 */

import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/ErrorOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef, useEffect, useRef } from 'react';
import { baseColors, gradients } from '../../theme/palette';
import { glowEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';

// ============================================================================
// Types
// ============================================================================

export type MutationState = 'idle' | 'loading' | 'success' | 'error';

export interface MutationButtonProps {
  /** Current mutation state */
  state: MutationState;

  /** Text displayed in idle state */
  idleText: string;

  /** Text displayed while loading */
  loadingText?: string;

  /** Text displayed on success */
  successText?: string;

  /** Text displayed on error */
  errorText?: string;

  /** Click handler (only active in idle state) */
  onClick?: () => void;

  /** Auto-reset to idle after success/error (ms) */
  autoResetDelay?: number;

  /** Callback when auto-reset triggers */
  onReset?: () => void;

  /** Button variant */
  variant?: 'gradient' | 'outlined' | 'subtle';

  /** Button size */
  size?: 'small' | 'medium' | 'large';

  /** Full width button */
  fullWidth?: boolean;

  /** Additional className */
  className?: string;

  /** Disabled state (overrides mutation state) */
  disabled?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledButtonProps {
  $variant: 'gradient' | 'outlined' | 'subtle';
  $state: MutationState;
  $isDark: boolean;
  $size: 'small' | 'medium' | 'large';
  $fullWidth: boolean;
}

const sizeStyles = {
  small: { padding: '8px 16px', fontSize: '0.8125rem', height: 36 },
  medium: { padding: '12px 24px', fontSize: '0.875rem', height: 44 },
  large: { padding: '16px 32px', fontSize: '1rem', height: 52 },
};

const StyledButton = styled(motion.button, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledButtonProps>(({ theme, $variant, $state, $isDark, $size, $fullWidth }) => {
  const glow = glowEffects.primary;
  const isSuccess = $state === 'success';
  const isError = $state === 'error';
  const isLoading = $state === 'loading';

  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    borderRadius: 12,
    fontWeight: 500,
    fontFamily: 'inherit',
    textTransform: 'none',
    transition: transitionPresets.button,
    cursor: isLoading ? 'wait' : 'pointer',
    outline: 'none',
    width: $fullWidth ? '100%' : 'auto',
    minWidth: 120,
    ...sizeStyles[$size],

    // Success state overrides
    ...(isSuccess && {
      background: baseColors.success[500],
      color: baseColors.neutral[0],
      border: 'none',
      boxShadow: `0 0 20px ${alpha(baseColors.success[500], 0.4)}`,
    }),

    // Error state overrides
    ...(isError && {
      background: baseColors.error[500],
      color: baseColors.neutral[0],
      border: 'none',
      boxShadow: `0 0 20px ${alpha(baseColors.error[500], 0.4)}`,
    }),

    // Normal variant styles (when not success/error)
    ...(!isSuccess &&
      !isError && {
        // Gradient variant
        ...($variant === 'gradient' && {
          background: gradients.buttonPrimary,
          color: baseColors.neutral[0],
          border: 'none',
          boxShadow: theme.shadows[2],

          '&:hover:not(:disabled)': {
            boxShadow: `${theme.shadows[4]}, ${glow.medium}`,
            transform: 'translateY(-1px)',
          },
        }),

        // Outlined variant
        ...($variant === 'outlined' && {
          background: 'transparent',
          color: baseColors.primary[$isDark ? 400 : 500],
          border: `1px solid ${alpha(baseColors.primary[$isDark ? 400 : 500], 0.4)}`,
          boxShadow: 'none',

          '&:hover:not(:disabled)': {
            background: alpha(baseColors.primary[500], 0.06),
            borderColor: alpha(baseColors.primary[$isDark ? 400 : 500], 0.6),
          },
        }),

        // Subtle variant
        ...($variant === 'subtle' && {
          background: 'transparent',
          color: $isDark ? baseColors.neutral[300] : baseColors.neutral[700],
          border: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
          boxShadow: 'none',

          '&:hover:not(:disabled)': {
            background: $isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
            borderColor: $isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
          },
        }),
      }),

    // Disabled state
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      transform: 'none',
    },
  };
});

const IconWrapper = styled(motion.span)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// ============================================================================
// Component
// ============================================================================

export const MutationButton = forwardRef<HTMLButtonElement, MutationButtonProps>(
  (
    {
      state,
      idleText,
      loadingText = 'Saving...',
      successText = 'Saved!',
      errorText = 'Failed',
      onClick,
      autoResetDelay = 2000,
      onReset,
      variant = 'gradient',
      size = 'medium',
      fullWidth = false,
      className,
      disabled,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Auto-reset after success or error
    useEffect(() => {
      if ((state === 'success' || state === 'error') && autoResetDelay > 0) {
        resetTimeoutRef.current = setTimeout(() => {
          onReset?.();
        }, autoResetDelay);
      }

      return () => {
        if (resetTimeoutRef.current) {
          clearTimeout(resetTimeoutRef.current);
        }
      };
    }, [state, autoResetDelay, onReset]);

    const getText = () => {
      switch (state) {
        case 'loading':
          return loadingText;
        case 'success':
          return successText;
        case 'error':
          return errorText;
        default:
          return idleText;
      }
    };

    const getIcon = () => {
      switch (state) {
        case 'loading':
          return <CircularProgress size={size === 'small' ? 14 : size === 'large' ? 20 : 16} color="inherit" />;
        case 'success':
          return <CheckIcon fontSize="small" />;
        case 'error':
          return <ErrorIcon fontSize="small" />;
        default:
          return null;
      }
    };

    const icon = getIcon();

    return (
      <StyledButton
        ref={ref}
        $variant={variant}
        $state={state}
        $isDark={isDark}
        $size={size}
        $fullWidth={fullWidth}
        className={className}
        disabled={disabled || state === 'loading'}
        onClick={state === 'idle' ? onClick : undefined}
        whileTap={state === 'idle' && !disabled ? { scale: 0.98 } : undefined}
      >
        <AnimatePresence mode="wait">
          {icon && (
            <IconWrapper
              key={state}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.15 }}
            >
              {icon}
            </IconWrapper>
          )}
        </AnimatePresence>
        <AnimatePresence mode="wait">
          <motion.span
            key={state}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
          >
            {getText()}
          </motion.span>
        </AnimatePresence>
      </StyledButton>
    );
  },
);

MutationButton.displayName = 'MutationButton';

export default MutationButton;
